// @ts-strict-ignore
import { CommonModule } from "@angular/common";
import { Component, OnInit } from "@angular/core";
import { FormsModule } from "@angular/forms";
import { ButtonModule } from "primeng/button";
import { InputNumberModule } from "primeng/inputnumber";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { TableModule } from "primeng/table";
import { ErrorCheck } from "src/app/shared/models/error-check.model";
import { SharedDirectivesModule } from "src/app/shared/shared-directives.module";
import { ErrorMessageComponent } from "src/app/modules/esim/shared/components/error-message/error-message.component";
import * as C from "../../../../../../shared/constants/globals.constants";
import { EWizDynamicBaseClassComponent } from "src/app/modules/esim/shared/classes/ewiz-dynamic.class";
import { DataService } from "src/app/shared/services/data.service";
import * as ProfileModel from "src/app/shared/models/profile.model";

/**
 * Characters an EF_P-CSCF FQDN holds. Each address is written as a TLV whose single short-form
 * BER length byte covers the address type byte as well, so anything past 126 would encode an
 * invalid length.
 */
export const PCSCF_FQDN_MAX_LENGTH = 126;

@Component({
  selector: C.EWIZ_PCSCF_CONFIGURATION,
  templateUrl: "./pcscf-configuration.component.html",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    SharedDirectivesModule,
    ErrorMessageComponent
  ]
})
export class EWizPCSCFConfigurationComponent extends EWizDynamicBaseClassComponent implements OnInit, ErrorCheck {
  constructor(private dataService: DataService) {
    super();

    this.addressTypes = [
      { label: $localize`FQDN`, value: "1" },
      { label: $localize`IPv4`, value: "2" },
      { label: $localize`IPv6`, value: "3" }
    ];
  }

  protected readonly fqdnMaxLength = PCSCF_FQDN_MAX_LENGTH;
  selectedAddresses: ProfileModel.PCSCFConfigurationDto[] | null = null;
  nextId = 0;
  addressTypes = [];

  ngOnInit() {
    this.errorCheck(this.profileValues, this.dataService);

    if (this.profileValues.isimConfiguration.pcscfConfiguration.length > 0) {
      this.nextId =
        +this.profileValues.isimConfiguration.pcscfConfiguration[this.profileValues.isimConfiguration.pcscfConfiguration.length - 1].id;
    }

    // Handle data migration for profiles that were created prior to changing PCSCFConfigurationDto
    this.migrateProfileData();
  }

  migrateProfileData() {
    const newConfiguration = [];

    for (const configuration of this.profileValues.isimConfiguration.pcscfConfiguration) {
      if (+configuration.addressType.value === 2 && !configuration.pcscfAddressIPv4) {
        newConfiguration.push({
          ...configuration,
          pcscfAddressIPv4: {
            address1: 0,
            address2: 0,
            address3: 0,
            address4: 0
          }
        });
      } else if (+configuration.addressType.value === 3 && !configuration.pcscfAddressIPv6) {
        newConfiguration.push({
          ...configuration,
          pcscfAddressIPv6: {
            address1: "0000",
            address2: "0000",
            address3: "0000",
            address4: "0000",
            address5: "0000",
            address6: "0000",
            address7: "0000",
            address8: "0000"
          }
        });
      } else {
        newConfiguration.push(configuration);
      }
    }

    this.profileValues.isimConfiguration.pcscfConfiguration = [...newConfiguration];
    this.handleFormEvents();
  }

  errorCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    // Always reset error values to default state prior to executing check
    this.errorFound = false;
    this.errorMessage = "";

    for (const configuration of values.isimConfiguration.pcscfConfiguration) {
      if (configuration.addressType === null) {
        this.errorMessage = $localize`All Address Types must be specified`;
        this.errorFound = true;
        break;
      } else {
        if (+configuration.addressType.value === 1) {
          if (configuration.pcscfAddressFQDN === null || typeof configuration.pcscfAddressFQDN !== "string") {
            this.errorMessage = $localize`All P-CSCF Addresses must be entered`;
            this.errorFound = true;
            break;
          } else if (configuration.pcscfAddressFQDN.length > PCSCF_FQDN_MAX_LENGTH) {
            this.errorMessage = $localize`P-CSCF Addresses must be between 1 and 126 characters`;
            this.errorFound = true;
            break;
          }
        } else if (
          +configuration.addressType.value === 2 &&
          (!configuration.pcscfAddressIPv4 ||
            dataService.isNullOrEmpty(configuration.pcscfAddressIPv4.address1) ||
            dataService.isNullOrEmpty(configuration.pcscfAddressIPv4.address2) ||
            dataService.isNullOrEmpty(configuration.pcscfAddressIPv4.address3) ||
            dataService.isNullOrEmpty(configuration.pcscfAddressIPv4.address4))
        ) {
          this.errorMessage = $localize`Invalid IPv4 address values, please ensure all address components are complete.`;
          this.errorFound = true;
          break;
        } else if (
          +configuration.addressType.value === 3 &&
          (!configuration.pcscfAddressIPv6 ||
            !dataService.validHexValue(configuration.pcscfAddressIPv6.address1, 2, 2) ||
            !dataService.validHexValue(configuration.pcscfAddressIPv6.address2, 2, 2) ||
            !dataService.validHexValue(configuration.pcscfAddressIPv6.address3, 2, 2) ||
            !dataService.validHexValue(configuration.pcscfAddressIPv6.address4, 2, 2) ||
            !dataService.validHexValue(configuration.pcscfAddressIPv6.address5, 2, 2) ||
            !dataService.validHexValue(configuration.pcscfAddressIPv6.address6, 2, 2) ||
            !dataService.validHexValue(configuration.pcscfAddressIPv6.address7, 2, 2) ||
            !dataService.validHexValue(configuration.pcscfAddressIPv6.address8, 2, 2))
        ) {
          this.errorMessage = $localize`Invalid IPv6 address values, please ensure all address components are complete.`;
          this.errorFound = true;
          break;
        }
      }
    }

    return this.errorFound;
  }

  addAddress() {
    this.profileValues.isimConfiguration.pcscfConfiguration.push({
      id: ++this.nextId,
      addressType: null,
      pcscfAddressFQDN: null,
      pcscfAddressIPv4: {
        address1: 0,
        address2: 0,
        address3: 0,
        address4: 0
      },
      pcscfAddressIPv6: {
        address1: "0000",
        address2: "0000",
        address3: "0000",
        address4: "0000",
        address5: "0000",
        address6: "0000",
        address7: "0000",
        address8: "0000"
      }
    });

    this.handleFormEvents();
  }

  deleteAddress() {
    const newAddresses = [];

    for (const originalAddress of this.profileValues.isimConfiguration.pcscfConfiguration) {
      let removeAddress = false;

      for (const selectedAddress of this.selectedAddresses) {
        if (originalAddress.id === selectedAddress.id) {
          removeAddress = true;
          break;
        }
      }

      if (!removeAddress) {
        newAddresses.push(originalAddress);
      }
    }

    this.profileValues.isimConfiguration.pcscfConfiguration = [...newAddresses];
    this.selectedAddresses = [];

    this.handleFormEvents();
  }
}
